import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Shield, Upload, Sparkles, Loader2, ExternalLink, FileSpreadsheet } from "lucide-react";
import { Button } from "./ui/button";
import { api, fileUrl } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { STATUS_LABELS, STATUS_STYLES } from "../lib/constants";
import { InsureNowBanner } from "./insurance/InsureNowBanner";
import { VehicleInsuranceDialog } from "./insurance/VehicleInsuranceDialog";
import { InsurerSheetsDialog } from "./insurance/InsurerSheetsDialog";

const TITLES = {
  all: "التأمين الطبي — كل الموظفين",
  individuals: "تأمين الأفراد",
  cars: "تأمين السيارات",
  moto: "تأمين الدراجات النارية",
};

const KPI = ({ label, value, tone }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`font-heading text-2xl font-bold ${tone || "text-slate-800"}`}>{value}</p>
  </div>
);

export const InsurancePanel = ({ type = "individuals", canEdit }) => {
  const { dir } = useLang();
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [importing, setImporting] = useState(false);
  const showVehicle = type !== "individuals";
  const isCars = type === "cars" || type === "moto";
  const [editVeh, setEditVeh] = useState(null);
  const [sheetsOpen, setSheetsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.insuranceList(type)); }
    catch { toast.error("تعذّر تحميل بيانات التأمين"); }
    finally { setLoading(false); }
  }, [type]);
  useEffect(() => { load(); }, [load]);

  const onPolicy = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setExtracting(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.insuranceExtractPolicy(type, fd);
      const nf = r.not_found?.length || 0;
      if (r.matched_count > 0) {
        toast.success(`تمت مطابقة ${r.matched_count} موظف من الوثيقة (من ${r.total_rows})${nf ? ` — لم يُطابق ${nf} رقم إقامة` : ""}`);
      } else {
        toast.warning(`لم يُطابق أي موظف${nf ? ` — ${nf} رقم إقامة غير موجود بالنظام` : ""}`);
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر قراءة البوليصة"); }
    finally { setExtracting(false); }
  };

  const [importRes, setImportRes] = useState(null);
  const onImport = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.insuranceImport(type, fd);
      toast.success(`تم تحديث تأمين ${r.updated} ${r.mode === "vehicles" ? "سيارة" : "موظف"}`);
      setImportRes(r);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر الاستيراد"); }
    finally { setImporting(false); }
  };

  const c = data.counts || {};

  return (
    <div className="space-y-4" dir={dir} data-testid={`insurance-panel-${type}`}>
      {importRes && (
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm text-slate-700 space-y-1" data-testid="insurance-import-result">
          <div className="flex justify-between gap-2"><b>نتيجة الاستيراد — {importRes.total_rows} صف في الملف</b>
            <button className="text-xs text-slate-500 hover:text-slate-800" onClick={() => setImportRes(null)} data-testid="insurance-import-result-close">إغلاق</button></div>
          {importRes.mode === "vehicles" ? <div>✔ تم تحديث تأمين <b>{importRes.updated}</b> سيارة (مطابقة برقم اللوحة)</div> :
          <div>✔ تم تحديث <b>{importRes.updated}</b> موظف: أفراد {importRes.by_type?.individuals || 0} · سيارات {importRes.by_type?.cars || 0} · دراجات {importRes.by_type?.moto || 0}
            <span className="text-slate-500"> (كل فئة تظهر في تبويبها)</span></div>}
          {importRes.no_date > 0 && <div className="text-amber-700">⚠ {importRes.no_date} صف بدون تاريخ تأمين في الملف — لم يُحدَّث</div>}
          {importRes.not_found?.length > 0 && <div className="text-rose-700">✖ {importRes.not_found.length} {importRes.mode === "vehicles" ? "لوحة غير موجودة في السيارات" : "رقم إقامة غير موجود في الموظفين"}:
            <span className="font-mono-nums text-xs select-all" dir="ltr"> {importRes.not_found.join("، ")}</span></div>}
        </div>
      )}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-600"><Shield className="h-5 w-5" /></div>
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-900">{TITLES[type]}</h2>
            <p className="text-xs text-slate-500">رفع البوليصة (PDF/صورة) أو Excel — نطابق برقم الإقامة ونحسب الانتهاء = السريان + سنة تلقائيًا</p>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <input type="file" accept="image/*,.pdf" id={`ins-policy-${type}`} className="hidden" data-testid="insurance-policy-input" onChange={onPolicy} />
            {!isCars && <Button variant="outline" disabled={extracting} onClick={() => document.getElementById(`ins-policy-${type}`).click()}
              className="gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid="insurance-policy-btn">
              {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} رفع بوليصة
            </Button>}
            {!isCars && <Button variant="outline" onClick={() => setSheetsOpen(true)}
              className="gap-1.5 border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="insurer-sheets-btn">
              <Sparkles className="h-4 w-4" /> شيت شركة التأمين (مطابقة/تعبئة)
            </Button>}
            <input type="file" accept=".xlsx,.xls" id={`ins-import-${type}`} className="hidden" data-testid="insurance-import-input" onChange={onImport} />
            <Button variant="outline" disabled={importing} onClick={() => document.getElementById(`ins-import-${type}`).click()}
              className="gap-1.5 border-teal-200 text-teal-700 hover:bg-teal-50" data-testid="insurance-import-btn">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} استيراد Excel
            </Button>
          </div>
        )}
      </div>

      {type !== "all" && <InsureNowBanner kind={type} canEdit={canEdit} />}

      <div className={`grid grid-cols-2 gap-3 ${c.registered > 0 ? "sm:grid-cols-5" : "sm:grid-cols-4"}`}>
        <KPI label="الإجمالي" value={c.total || 0} />
        <KPI label="سارية" value={c.valid || 0} tone="text-blue-600" />
        <KPI label="تقترب من الانتهاء" value={c.expiring_soon || 0} tone="text-amber-600" />
        <KPI label="منتهية / غير مُدخلة" value={(c.expired || 0) + (c.none || 0) - data.items.filter((i) => i.status === "none" && i.insured).length} tone="text-red-600" />
        {c.registered > 0 && <KPI label="مسجّلين لدى شركة التأمين" value={c.registered} tone="text-emerald-600" />}
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-50 text-slate-500"><tr>
              {isCars ? <><th className="px-3 py-2.5 text-right">رقم اللوحة</th><th className="px-3 py-2.5">نوع المركبة</th></> : <>
              <th className="px-3 py-2.5 text-right">الموظف</th>
              <th className="px-3 py-2.5">رقم الإقامة</th>
              {showVehicle && <th className="px-3 py-2.5">المركبة</th>}</>}
              <th className="px-3 py-2.5">تاريخ السريان</th>
              <th className="px-3 py-2.5">تاريخ الانتهاء</th>
              <th className="px-3 py-2.5">الحالة</th>
              <th className="px-3 py-2.5">البوليصة</th>
            </tr></thead>
            <tbody>
              {data.items.length === 0 ? (
                <tr><td colSpan={showVehicle ? 7 : 6} className="py-12 text-center text-slate-400">{isCars ? "لا توجد سيارات مسجّلة" : "لا يوجد موظفون في هذا القسم"}</td></tr>
              ) : data.items.map((r) => (
                <tr key={r.id} data-testid={`insurance-row-${r.id}`} className="border-t border-slate-100">
                  {isCars ? <>
                    <td className="px-3 py-2.5 text-right font-semibold text-slate-800" dir="ltr">{r.vehicle_number}</td>
                    <td className="px-3 py-2.5 text-center text-slate-600">{[r.vehicle_type, r.model].filter(Boolean).join(" · ") || "—"}{r.branch ? <span className="text-xs text-slate-400"> · {r.branch}</span> : ""}</td>
                  </> : <>
                  <td className="px-3 py-2.5 text-right font-semibold text-slate-800">{r.name}{r.branch ? <span className="text-xs text-slate-400"> · {r.branch}</span> : ""}</td>
                  <td className="px-3 py-2.5 text-center font-mono-nums text-slate-600" dir="ltr">{r.iqama_number || "—"}</td>
                  {showVehicle && <td className="px-3 py-2.5 text-center text-slate-600" dir="ltr">{r.vehicle_number || "—"}</td>}</>}
                  <td className="px-3 py-2.5 text-center font-mono-nums text-slate-600" dir="ltr">{r.start_date || "—"}</td>
                  <td className="px-3 py-2.5 text-center font-mono-nums text-slate-600" dir="ltr">{r.expiry_date || "—"}</td>
                  <td className="px-3 py-2.5 text-center">
                    {r.status === "none" && r.insured ? (
                      <span className="inline-block rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700" data-testid={`insurance-registered-${r.id}`}>
                        مسجّل لدى التأمين{r.insurer ? ` (${r.insurer})` : ""}
                      </span>
                    ) : (
                    <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-semibold ${STATUS_STYLES[r.status]}`}>
                      {STATUS_LABELS[r.status]}{r.status === "expiring_soon" && r.days_left != null ? ` (${r.days_left} يوم)` : ""}
                    </span>)}
                    {r.ins_class && <div className="mt-0.5 text-[10.5px] text-slate-400" dir="auto">فئة {r.ins_class}{r.ins_premium ? ` · ${r.ins_premium} ر.س` : ""}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <div className="flex items-center justify-center gap-2">
                    {r.attachment_id ? (
                      <a href={fileUrl(r.attachment_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs">
                        <ExternalLink className="h-3.5 w-3.5" /> عرض
                      </a>
                    ) : <span className="text-slate-300">—</span>}
                    {isCars && canEdit && <button data-testid={`vehicle-ins-edit-${r.id}`} onClick={() => setEditVeh(r)}
                      className="inline-flex items-center gap-1 rounded-md border border-teal-200 px-2 py-0.5 text-xs text-teal-700 hover:bg-teal-50"><Upload className="h-3 w-3" /> {r.own_policy ? "تعديل" : "رفع بوليصة"}</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="flex items-center gap-1.5 text-xs text-slate-400"><FileSpreadsheet className="h-3.5 w-3.5" /> {isCars ? "ملف Excel: عمود «رقم اللوحة» + عمود «تاريخ السريان» أو «تاريخ الانتهاء» (وعمود «رقم البوليصة» اختياري) — اللوحة تُقرأ بالعربي أو الإنجليزي." : "ملف Excel: عمود «رقم الإقامة» + عمود «تاريخ السريان» (وعمود «تاريخ الانتهاء» اختياري)."}</p>
      {sheetsOpen && <InsurerSheetsDialog open onClose={() => setSheetsOpen(false)} onSaved={load} />}
      {editVeh && <VehicleInsuranceDialog vehicle={editVeh} onClose={() => setEditVeh(null)} onSaved={() => { setEditVeh(null); load(); }} />}
    </div>
  );
};
